import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ExternalLink, Globe, Trophy, ArrowRight, Star, Home, GraduationCap, ShoppingBag, Code } from 'lucide-react';
import { apiService } from '../services/api';

const ProjectsPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentYear] = useState(new Date().getFullYear());
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await apiService.getProjects();
        if (response.success && response.data) {
          setProjects(Array.isArray(response.data) ? response.data : []);
        } else {
          setError(response.error || 'Erreur lors du chargement des projets');
        }
      } catch (err) {
        setError('Erreur de connexion à l\'API');
        console.error('Erreur lors du chargement des projets:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const navigation = [
    { path: '/', label: 'Accueil', icon: Home },
    { path: '/formations', label: 'Formations', icon: GraduationCap },
    { path: '/competitions', label: 'Compétitions', icon: Trophy },
    { path: '/projets', label: 'Projets', icon: Code },
    { path: '/boutique', label: 'Boutique', icon: ShoppingBag }
  ];

  // Helper function to get appropriate icon for a project
  const getProjectIcon = (iconString: string) => {
    // If it's an emoji, return it as text
    if (iconString && iconString.length <= 4) {
      return <span className="text-2xl">{iconString}</span>;
    }
    // Default icons based on project title or type
    return <Code className="w-8 h-8" />;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-lg py-3 shadow-lg shadow-black/50' : 'bg-gradient-to-b from-black/80 to-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/assets/logos/Phoelynx_Transparent.webp"
              alt="Phoelynx France"
              className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
            />
            <span className="lg:text-2xl md:text-xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
              Phoelynx France
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center lg:space-x-8 md:space-x-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-1 lg:space-x-2 lg:px-4 md:px-2 py-2 rounded-lg transition-all hover:bg-white/10 ${
                    item.path === '/projets' ? 'bg-orange-500/20 text-orange-300' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="lg:text-base md:text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-white/10">
            <div className="flex flex-col space-y-2 pt-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all hover:bg-white/10 ${
                      item.path === '/projets' ? 'bg-orange-500/20 text-orange-300' : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-900/20 via-black/50 to-black" />

        {/* Grid Background Effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 grid-background" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/20 rounded-full mb-6">
            <Star className="w-4 h-4 text-sky-400" />
            <span className="text-sm text-sky-400">Réalisations Phoelynx France</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-400 via-sky-400 to-red-400">
              NOS PROJETS
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Découvrez nos réalisations web et nos projets associatifs.
            Des solutions techniques modernes au service de la communauté.
          </p>

          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-red-500 text-white font-semibold rounded-full hover:bg-red-500/10 transition-all transform hover:scale-105">
            <ArrowRight className="w-5 h-5 rotate-180" />
            RETOUR À L'ACCUEIL
          </Link>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="text-red-400 mb-4">⚠️ Erreur de chargement</div>
              <p className="text-gray-400">{error}</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20">
              <Code className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">Aucun projet</h3>
              <p className="text-gray-500">Les projets seront bientôt disponibles.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {projects.map((project) => (
              <div key={project.id} className="group relative">
                <div className={`h-full bg-gradient-to-br ${project.color} p-[1px] rounded-2xl`}>
                  <div className="h-full bg-black rounded-2xl p-8 hover:bg-gray-900/50 transition-all duration-300">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${project.color} mb-4`}>
                        {getProjectIcon(project.icon)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-3 py-1 bg-green-600/20 text-green-400 rounded-full">
                          {project.status}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-3xl font-bold mb-2">{project.title}</h3>
                    <p className="text-lg text-sky-400 mb-4">{project.subtitle}</p>
                    <p className="text-gray-300 mb-6 leading-relaxed">{project.description}</p>

                    {/* Features */}
                    {project.features && project.features.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold mb-3 text-white">Fonctionnalités principales :</h4>
                        <ul className="space-y-2">
                          {project.features.map((feature: string, idx: number) => (
                            <li key={idx} className="text-sm text-gray-300 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Technologies */}
                    {project.technologies && project.technologies.length > 0 && (
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold mb-3 text-white">Technologies utilisées :</h4>
                        <div className="flex flex-wrap gap-2">
                          {project.technologies.map((tech: string, idx: number) => (
                            <span key={idx} className="text-xs px-3 py-1 bg-gray-800 text-gray-300 rounded-full">
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* CTA */}
                    <div className="flex flex-col sm:flex-row gap-4">
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`group/btn flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r ${project.color} text-white font-semibold rounded-full hover:shadow-lg hover:shadow-red-500/25 transition-all transform hover:scale-105`}
                      >
                        <Globe className="w-4 h-4" />
                        VISITER LE SITE
                        <ExternalLink className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </a>
                      <button className="px-6 py-3 border-2 border-gray-700 text-gray-300 font-semibold rounded-full hover:border-red-500 hover:text-sky-400 transition-all">
                        PLUS D'INFOS
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            </div>
          )}

          {/* Call to Action */}
          <div className="text-center mt-16 p-8 border border-gray-800 rounded-2xl bg-gray-900/20">
            <h3 className="text-2xl font-bold mb-4">Vous avez un projet ?</h3>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
              Que ce soit pour un site web, une application ou l'administration de vos systèmes,
              nous sommes là pour vous accompagner dans la réalisation de vos projets techniques.
            </p>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-red-500/25 transition-all transform hover:scale-105"
            >
              DISCUTER DE VOTRE PROJET
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/logos/Phoelynx_Transparent.webp"
                alt="Phoelynx France"
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
                Phoelynx France
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Globe className="w-5 h-5 text-gray-400 hover:text-red-400 cursor-pointer transition-colors" />
              <span className="text-gray-400">phoelynx.fr</span>
            </div>

            <div className="flex flex-col items-center md:items-end gap-2">
              <p className="text-gray-400 text-sm">© 2025-{currentYear} Phoelynx France. Tous droits réservés.</p>
              <div className="flex gap-4 text-xs">
                <Link to="/mentions-legales" className="text-gray-500 hover:text-red-400 transition-colors">
                  Mentions légales
                </Link>
                <Link to="/politique-confidentialite" className="text-gray-500 hover:text-red-400 transition-colors">
                  Confidentialité
                </Link>
                <Link to="/conditions-utilisation" className="text-gray-500 hover:text-red-400 transition-colors">
                  CGU
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProjectsPage;